/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signOut,
  deleteUser,
  type AuthCredential,
  type User,
} from "firebase/auth";
import { ref, get, update } from "firebase/database";
import { getStorage, ref as storageRef, deleteObject } from "firebase/storage";
import * as Sentry from "@sentry/react";
import { auth, db } from "@/config/firebase";
import { setUserContext, clearUserContext } from "@/utils/crashlytics";
import { logUserAction } from "@/utils/errorLogger";
import { ensureUserProfile } from "@/utils/ensureUserProfile";
import { claimPhoneIndex } from "@/utils/phoneIndex";
import { signInWithPhoneCredential } from "@/lib/authProviders";
import {
  clearLegacyEula,
  clearLocalEula,
  readLocalEula,
  readRemoteEula,
  writeLocalEula,
  writeRemoteEula,
} from "@/lib/eulaStore";
import {
  recordVerifyReentrancySkipped,
  recordProfileEnsureFailed,
  type OtpInstrumentationSink,
} from "@/utils/safecircle/otpInstrumentation";

// Sentry adapter for the pure OTP instrumentation helpers.
const otpSink: OtpInstrumentationSink = {
  addBreadcrumb: (b) => Sentry.addBreadcrumb(b),
  captureMessage: (message, ctx) => Sentry.captureMessage(message, ctx),
  captureException: (err, ctx) => Sentry.captureException(err, ctx),
};

// ── State machine ─────────────────────────────────────────────────────────────
//
//   bootstrapping ─┬─ listener(null) / 5s timeout ─▶ idle ──verifyPhone()──┐
//                  └─ listener(user) ──────────────────────────────────────┤
//                                                                          ▼
//   idle ──verifyPhone()──▶ authenticating ──cred ok──▶ checkingProfile ──▶ checkingEula
//     ▲  ▲                       │                                           │
//     │  └── cred reject ────────┘                         ┌─ accepted ──────┤──▶ authenticated
//     │                                                    ├─ not accepted ──┴──▶ eulaRequired
//     └── logout / decline ◀── any ─────────────────────────┴─ timeout/err ─────▶ error
//
// MyAmbits is phone-only: a verified phone credential IS the identity. There
// is NO OAuth/email provider and no link-on-collision machinery — Firebase
// Auth enforces one account per phone number, so signInWithCredential lands
// deterministically on the right account (creating it on first verification).
//
// There are NO conditional render booleans (`loading && !eulaSigned`). The
// UI switches on `state`. `loading` / `authResolved` are derived for the
// legacy `useAuth()` consumers.

export type AuthState =
  | "bootstrapping"
  | "idle"
  | "authenticating"
  | "checkingProfile"
  | "checkingEula"
  | "eulaRequired"
  | "authenticated"
  | "error";

const BOOTSTRAP_TIMEOUT_MS = 5000;
const EULA_TIMEOUT_MS = 5000;

// States during which the onAuthStateChanged listener must NOT start a new
// pipeline — an action is already driving the machine.
const IN_FLIGHT: ReadonlySet<AuthState> = new Set<AuthState>([
  "authenticating",
  "checkingProfile",
  "checkingEula",
]);

interface AuthContextType {
  user: User | null;
  /** Explicit state-machine value. Prefer this over the derived booleans. */
  state: AuthState;
  /** Last surfaced error message (credential reject or EULA failure). */
  error: string | null;
  /** Derived: true while the machine has not reached a settled state. */
  loading: boolean;
  /**
   * Derived: false only during `bootstrapping`. Gate cold-launch UI on this
   * so the phone sign-in screen never flashes while Firebase rehydrates.
   */
  authResolved: boolean;
  needsUsername: boolean;
  /** True once ensureUserProfile has resolved for the current user. */
  profileResolved: boolean;
  /** True once the active user's EULA acceptance is confirmed. */
  eulaAccepted: boolean;
  markUsernameClaimed: () => void;
  /**
   * PRIMARY sign-in: exchange a verified phone credential for a Firebase
   * `User` (creating the account on first verification), then claim the
   * /phoneIndex row and write /users/{uid}/phoneNumber so the gate detects
   * verification. Phone is the sole identity provider.
   */
  verifyPhone: (phoneCred: AuthCredential, e164: string) => Promise<void>;
  acceptEula: () => Promise<void>;
  declineEula: () => Promise<void>;
  retryEula: () => Promise<void>;
  /**
   * Sign out. Default ("soft") keeps the Firebase Auth session alive and only
   * clears the app-level UI state — so a returning user re-entering within the
   * same app run lands back authenticated WITHOUT a fresh SMS verification
   * (avoids tripping Firebase's `auth/too-many-requests` on logout/login
   * cycles). Pass `{ hard: true }` to fully `signOut(auth)` — used by "Switch
   * account" and account deletion, where the next user must re-verify.
   *
   * Note: on native iOS the Firebase session uses inMemoryPersistence, so a
   * soft logout only survives until the app is killed; across a cold launch
   * the user re-verifies regardless. The win is the in-session cycle.
   */
  logout: (opts?: { hard?: boolean }) => Promise<void>;
  /**
   * Re-enter using the still-live Firebase session left by a soft logout (no
   * SMS). Resolves true if a session was resumed, false if none exists (the
   * caller should then run the normal phone-verification flow). Used by the
   * "Continue as +1 …" affordance on the logged-out phone screen.
   */
  resumeSession: () => Promise<boolean>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [state, setState] = useState<AuthState>("bootstrapping");
  const [error, setError] = useState<string | null>(null);
  const [needsUsername, setNeedsUsername] = useState(false);
  const [profileResolved, setProfileResolved] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);

  // Refs mirror state for use inside long-lived callbacks/listeners — this
  // is the stale-closure class the RCA flagged; reading refs avoids it.
  const stateRef = useRef<AuthState>("bootstrapping");
  const userRef = useRef<User | null>(null);
  // Bumped on every new pipeline / logout so stale async steps self-cancel.
  const pipelineTokenRef = useRef(0);

  // True after a SOFT logout: the Firebase session is intentionally kept alive
  // but the user is "logged out" at the UI level. Suppresses the
  // onAuthStateChanged listener from re-driving the pipeline back to
  // authenticated (e.g. on a token-refresh emit), which would silently undo the
  // logout. Cleared when the user resumes (Continue as), hard-logs-out, or a
  // fresh credential exchange runs.
  const softLoggedOutRef = useRef(false);

  const setUser = useCallback((u: User | null) => {
    userRef.current = u;
    setUserState(u);
  }, []);

  const transition = useCallback(
    (next: AuthState, data?: Record<string, unknown>) => {
      const from = stateRef.current;
      if (from === next) return;
      stateRef.current = next;
      setState(next);
      console.debug(`[auth] ${from} → ${next}`, data ?? {});
      Sentry.addBreadcrumb({
        category: "auth",
        message: `state:${from}->${next}`,
        level: "info",
        data,
      });
    },
    [],
  );

  const resolveEula = useCallback(
    async (uid: string, token: number) => {
      transition("checkingEula", { uid });
      clearLegacyEula();

      const local = readLocalEula(uid);
      if (local) {
        if (token !== pipelineTokenRef.current) return;
        setEulaAccepted(true);
        transition("authenticated", { via: "eula:local" });
        // Background-sync to RTDB if it's missing there.
        void readRemoteEula(db, uid)
          .then((remote) => {
            if (!remote) void writeRemoteEula(db, uid, local).catch(() => {});
          })
          .catch(() => {});
        return;
      }

      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const remote = await Promise.race<string | null>([
          readRemoteEula(db, uid),
          new Promise<never>((_, reject) => {
            timer = setTimeout(
              () => reject(new Error("eula-timeout")),
              EULA_TIMEOUT_MS,
            );
          }),
        ]);
        if (timer) clearTimeout(timer);
        if (token !== pipelineTokenRef.current) return;
        if (remote) {
          writeLocalEula(uid, remote);
          setEulaAccepted(true);
          transition("authenticated", { via: "eula:remote" });
        } else {
          setEulaAccepted(false);
          transition("eulaRequired");
        }
      } catch (err) {
        if (timer) clearTimeout(timer);
        if (token !== pipelineTokenRef.current) return;
        const timedOut = err instanceof Error && err.message === "eula-timeout";
        setError(
          timedOut
            ? "Couldn't verify your agreement status. Check your connection and tap retry."
            : "Couldn't load the agreement. Please tap retry.",
        );
        transition("error", {
          reason: timedOut ? "eula-timeout" : "eula-read-failed",
        });
      }
    },
    [transition],
  );

  const runPostCredentialPipeline = useCallback(
    async (u: User, origin: "action" | "listener") => {
      const token = ++pipelineTokenRef.current;
      setUser(u);
      setUserContext(u.uid, u.email ?? undefined);

      // Anonymous sessions are ephemeral readers — InviteLanding signs the
      // visitor in anon just to read /inviteTokens/$token under the auth-
      // required RTDB rule. They are NOT committed accounts: do not create
      // a /users/{uid} profile node for them and do not require EULA
      // acceptance. Without this short-circuit App.tsx's eulaRequired
      // branch renders EulaScreen over /invite/:token, occluding the
      // invite card the visitor came to see.
      if (u.isAnonymous) {
        setProfileResolved(true);
        setNeedsUsername(false);
        transition("authenticated", { origin, uid: u.uid, anonymous: true });
        return;
      }

      setProfileResolved(false);
      transition("checkingProfile", { origin, uid: u.uid });

      try {
        const { needsUsername: needs } = await ensureUserProfile(db, u);
        if (token !== pipelineTokenRef.current) return;
        setNeedsUsername(needs);
      } catch (err) {
        if (token !== pipelineTokenRef.current) return;
        console.error("[AuthContext] ensureUserProfile error:", err);
        // Fail-open: don't strand the user if the profile write fails. But this
        // was console-only — a profile write failure was invisible in Sentry.
        // Capture it (logging only; the fail-open behaviour is unchanged).
        recordProfileEnsureFailed(otpSink, err, { uid: u.uid });
        setNeedsUsername(false);
      } finally {
        if (token === pipelineTokenRef.current) setProfileResolved(true);
      }

      if (token !== pipelineTokenRef.current) return;
      await resolveEula(u.uid, token);
    },
    [transition, resolveEula, setUser],
  );

  const markUsernameClaimed = useCallback(() => {
    setNeedsUsername(false);
  }, []);

  // PRIMARY sign-in. Exchange the verified phone credential for a Firebase
  // User (Firebase Auth creates the account on first verification, or returns
  // the existing one — one account per phone number, so no duplicate is
  // possible), then mirror ownership into /phoneIndex + /users/{uid}/
  // phoneNumber so useVerifiedPhone reports verified and the gate unmounts.
  const verifyPhone = useCallback(
    async (phoneCred: AuthCredential, e164: string) => {
      if (IN_FLIGHT.has(stateRef.current)) {
        // Re-entered while a sign-in is already in flight: this returns early
        // and resolves as if it succeeded, while doing nothing. Capture the
        // silent no-op (the caller's confirm button would just clear its
        // spinner with no advance and no error).
        recordVerifyReentrancySkipped(otpSink, stateRef.current);
        return;
      }
      softLoggedOutRef.current = false; // a real sign-in — clear any soft-out
      setError(null);
      transition("authenticating", { method: "phone" });
      try {
        const u = await signInWithPhoneCredential(phoneCred);

        // SIGN-IN HAS SUCCEEDED at this point (Firebase Auth owns the phone on
        // u.uid). Everything below is a best-effort RTDB MIRROR — it must NEVER
        // strand a validly-signed-in user. Mirror the phone into /phoneIndex +
        // /users/{uid}/phoneNumber so the gate unmounts.
        //
        // claimPhoneIndex normally resolves to `claimed` (first time) or `self`
        // (re-verify). Two non-fatal exceptions, both handled by self-writing
        // the phoneNumber field and continuing:
        //   - `collision`: /phoneIndex points at a different live uid (stale
        //     data a cleanup script reconciles).
        //   - the transaction is REJECTED by the security rule with
        //     permission_denied — happens when the row is already owned by a
        //     different live uid (e.g. a prior test sign-in). runTransaction
        //     surfaces the rule rejection as a throw; we must catch it here so a
        //     signed-in user isn't bounced back to the login screen.
        try {
          const claim = await claimPhoneIndex(db, e164, u.uid);
          if (claim.kind === "collision") {
            Sentry.captureMessage(
              "phoneIndex points at a live different uid after phone sign-in",
              {
                level: "warning",
                tags: { area: "auth.phone" },
                extra: { e164, ownerUid: u.uid, indexUid: claim.existingUid },
              },
            );
            await update(ref(db), { [`users/${u.uid}/phoneNumber`]: e164 });
          } else if (claim.kind !== "claimed") {
            // `self`: index already points here; just write the phoneNumber
            // field so the gate unmounts (self-write permitted by rules).
            await update(ref(db), { [`users/${u.uid}/phoneNumber`]: e164 });
          }
        } catch (claimErr) {
          // The /phoneIndex transaction was denied (rule rejected the write —
          // the row belongs to another live uid) or otherwise failed. The user
          // is still signed in; do NOT throw. Self-write the phoneNumber field
          // so the gate clears, warn for observability, and continue.
          console.warn(
            "[auth] phoneIndex claim failed (non-fatal, user is signed in):",
            (claimErr as Error)?.message ?? claimErr,
          );
          Sentry.captureMessage("phoneIndex claim denied after phone sign-in", {
            level: "warning",
            tags: { area: "auth.phone" },
            extra: {
              e164,
              ownerUid: u.uid,
              error: (claimErr as Error)?.message ?? String(claimErr),
            },
          });
          await update(ref(db), { [`users/${u.uid}/phoneNumber`]: e164 }).catch(
            (writeErr) => {
              console.warn(
                "[auth] phoneNumber self-write also failed (continuing):",
                writeErr,
              );
            },
          );
        }

        // Profile + EULA gate. runPostCredentialPipeline owns the
        // user/state transitions from here.
        await runPostCredentialPipeline(u, "action");
      } catch (err) {
        const code =
          (err as { code?: string })?.code ??
          (err as { name?: string })?.name ??
          "unknown";
        console.error(
          `[auth] verifyPhone failed code=${code} msg=`,
          (err as Error)?.message ?? err,
        );
        // Drop back to idle so the sign-in screen / phone sheet re-renders
        // and the user can retry; surface the message for the sheet's UI.
        transition("idle", { method: "phone", failed: true });
        const message =
          err instanceof Error
            ? err.message.replace("Firebase: ", "")
            : "Sign in failed";
        setError(message);
        throw err;
      }
    },
    [transition, runPostCredentialPipeline],
  );

  const logout = useCallback(
    async (opts?: { hard?: boolean }) => {
      logUserAction("sign-out");
      // HARD logout: fully revoke the Firebase session so the next user must
      // re-verify (Switch account / account deletion). SOFT logout (default):
      // keep auth.currentUser alive and only clear the app UI — re-entry within
      // the same app run skips SMS. The route tree gates LoggedOutFlow on the
      // local `user` state (App.tsx), so nulling it here returns the visitor to
      // Welcome regardless of the underlying Firebase session.
      if (opts?.hard) {
        await signOut(auth);
        softLoggedOutRef.current = false; // session is truly gone
      } else {
        // Keep the Firebase session but suppress the listener from re-driving
        // the pipeline back to authenticated on a later token-refresh emit.
        softLoggedOutRef.current = true;
      }
      pipelineTokenRef.current++; // cancel any in-flight pipeline
      // Detach the Sentry uid here explicitly: a SOFT logout keeps the Firebase
      // session alive, so onAuthStateChanged never fires null and can't clear it
      // for us — without this a stale uid would be attributed to the logged-out
      // session and to the next account that signs in on this device.
      clearUserContext();
      setUser(null);
      setNeedsUsername(false);
      setProfileResolved(false);
      setEulaAccepted(false);
      transition("idle", { reason: opts?.hard ? "logout-hard" : "logout-soft" });
    },
    [transition, setUser],
  );

  // One-tap re-entry after a SOFT logout. The Firebase session is still alive
  // (auth.currentUser non-null), so re-drive the post-credential pipeline
  // directly — no new SMS verification. Returns false if there is no live
  // session to resume (caller then falls through to the normal phone flow).
  const resumeSession = useCallback(async (): Promise<boolean> => {
    const current = auth.currentUser;
    if (!current) return false;
    softLoggedOutRef.current = false; // re-entering — let the pipeline run
    setError(null);
    transition("authenticating", { method: "resume" });
    await runPostCredentialPipeline(current, "action");
    return true;
  }, [transition, runPostCredentialPipeline]);

  const acceptEula = useCallback(async () => {
    const u = userRef.current;
    if (!u) return;
    const timestamp = new Date().toISOString();
    writeLocalEula(u.uid, timestamp);
    setEulaAccepted(true);
    transition("authenticated", { via: "eula:accept" });
    try {
      await writeRemoteEula(db, u.uid, timestamp);
    } catch (err) {
      // Local acceptance already gates the UI; RTDB will backfill later.
      console.debug("[AuthContext] writeRemoteEula failed (kept local):", err);
    }
  }, [transition]);

  const declineEula = useCallback(async () => {
    const u = userRef.current;
    if (u) clearLocalEula(u.uid);
    setEulaAccepted(false);
    // Declining the terms must fully revoke the session — a user who refused
    // the EULA should not keep a live Firebase session for one-tap re-entry.
    await logout({ hard: true });
  }, [logout]);

  const retryEula = useCallback(async () => {
    const u = userRef.current;
    if (!u) {
      transition("idle", { reason: "retry-no-user" });
      return;
    }
    const token = ++pipelineTokenRef.current;
    await resolveEula(u.uid, token);
  }, [transition, resolveEula]);

  const deleteAccount = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("No user signed in");
    const uid = currentUser.uid;

    // Capture the username before wiping the profile so we can also
    // release the /usernames/{name} claim — without this the @handle
    // stays permanently un-claimable. Best-effort: skip on read failure.
    let username: string | null = null;
    try {
      const snap = await get(ref(db, `users/${uid}/profile/username`));
      const v = snap.val();
      if (typeof v === "string" && v.length > 0) username = v;
    } catch {
      /* best-effort */
    }

    // Build 117 — capture the user's verified phone (if any) so the
    // /phoneIndex/{e164} entry can be released in the same atomic
    // delete below. Skipping this is what caused the "delete account
    // → re-sign-up → infinite EULA loop": the orphaned phoneIndex
    // entry made PhoneVerificationSheet's pre-check think a canonical
    // account still owned the number, triggering the link-on-collision
    // flow which signed the user in as a phantom phone-only Auth
    // record with no EULA acceptance, looping the user back to the
    // EULA screen forever.
    let phoneE164: string | null = null;
    try {
      const snap = await get(ref(db, `users/${uid}/phoneNumber`));
      const v = snap.val();
      if (typeof v === "string" && v.startsWith("+")) phoneE164 = v;
    } catch {
      /* best-effort */
    }

    // Best-effort avatar removal. Must run while the user is still
    // authenticated (Storage rules require the owner). A missing file
    // or transient failure is fine — account deletion is the priority.
    try {
      await deleteObject(
        storageRef(getStorage(), `avatars/${uid}/profile.jpg`),
      );
    } catch {
      /* best-effort — file may not exist */
    }

    // CRITICAL self-owned delete — these paths are gated only by
    // `auth.uid === uid`, so they always succeed for the account owner. Done
    // as one atomic write so we never leave the user-data half-removed.
    const ownUpdates: Record<string, null> = {
      [`users/${uid}`]: null,
      [`presence/${uid}`]: null,
      [`friends/${uid}`]: null,
    };
    await update(ref(db), ownUpdates);

    // BEST-EFFORT index releases. The /usernames and /phoneIndex release rules
    // require the row to currently point at THIS uid. A stale or foreign row
    // (e.g. a prior account that owned the same handle/number and was deleted
    // without reconciliation) makes that write PERMISSION_DENIED. That must NOT
    // block the user from deleting their own account — so we release these
    // separately and swallow failures. A leftover index row is reconciled by
    // the phoneIndex/username claim logic on the next sign-in, not here.
    // (Previously these were bundled into the atomic write above, so a stale
    // index row failed the ENTIRE delete with "server permission error".)
    if (username) {
      await update(ref(db), { [`usernames/${username}`]: null }).catch((err) => {
        console.warn("[AuthContext] username release failed (non-fatal):", err);
      });
    }
    if (phoneE164) {
      await update(ref(db), { [`phoneIndex/${phoneE164}`]: null }).catch(
        (err) => {
          console.warn("[AuthContext] phoneIndex release failed (non-fatal):", err);
        },
      );
    }

    await deleteUser(currentUser);
    pipelineTokenRef.current++;
    clearUserContext();
    setUser(null);
    setNeedsUsername(false);
    setProfileResolved(false);
    setEulaAccepted(false);
    transition("idle", { reason: "account-deleted" });
  }, [transition, setUser]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      console.debug("[auth] onAuthStateChanged:", u?.uid ?? "null");
      const s = stateRef.current;
      if (u) {
        // A sign-in action is already driving the machine — let it own
        // the pipeline (deterministic) rather than racing the listener.
        if (IN_FLIGHT.has(s)) return;
        // Soft logout deliberately keeps this session alive while showing the
        // logged-out flow. Don't let a token-refresh emit re-drive the user
        // back to authenticated — only an explicit resume/verify clears this.
        if (softLoggedOutRef.current) return;
        // Already settled on this user (token refresh, duplicate emit).
        if (
          (s === "authenticated" || s === "eulaRequired" || s === "error") &&
          userRef.current?.uid === u.uid
        ) {
          return;
        }
        // Cold-launch rehydrate or an external sign-in.
        void runPostCredentialPipeline(u, "listener");
      } else {
        // No session. Don't clobber an in-flight credential exchange.
        if (s === "authenticating") return;
        pipelineTokenRef.current++;
        clearUserContext();
        setUser(null);
        setNeedsUsername(false);
        setProfileResolved(false);
        setEulaAccepted(false);
        transition("idle", { reason: "listener-null" });
      }
    });

    // WKWebView + inMemoryPersistence can silently stall the listener on
    // cold launch. Don't hang on a spinner: fall to `idle` so the phone
    // sign-in screen renders. Sign-in no longer depends on the listener
    // firing.
    const bootstrapTimeout = setTimeout(() => {
      if (stateRef.current === "bootstrapping") {
        Sentry.addBreadcrumb({
          category: "auth",
          message: "bootstrap_timeout",
          level: "warning",
        });
        transition("idle", { reason: "bootstrap-timeout" });
      }
    }, BOOTSTRAP_TIMEOUT_MS);

    return () => {
      unsub();
      clearTimeout(bootstrapTimeout);
    };
  }, [transition, runPostCredentialPipeline, setUser]);

  const loading = IN_FLIGHT.has(state) || state === "bootstrapping";
  const authResolved = state !== "bootstrapping";

  return (
    <AuthContext.Provider
      value={{
        user,
        state,
        error,
        loading,
        authResolved,
        needsUsername,
        profileResolved,
        eulaAccepted,
        markUsernameClaimed,
        verifyPhone,
        acceptEula,
        declineEula,
        retryEula,
        logout,
        resumeSession,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
